import { allocateInvoice, type InvoiceLineKind } from "@/lib/invoices";

// ─── UANG YANG SUDAH MASUK KE NOTA BARANG, BELUM GENAP SATU PCS ──────────────
//
// Audit pemilik 2026-10-24 ("cek apakah nominalnya akurat"): pcs baru jadi
// lunas (`paid_qty`) bila uang di barisnya GENAP satu pcs — mesin alokasi
// `compute_invoice_allocation` menutup `floor((kredit DP + uang) × qty / harga)`.
// Sisanya diam di nota tanpa jejak di keranjang, sehingga Rekap Barang WA,
// kotak ringkasan keranjang (pembeli & admin), dan kartu board menulis pcs itu
// SEHARGA PENUH. PROD 2026-10-24:
//   • Kredit DP lunas di nota open/partial yang belum jadi pcs: 9 nota,
//     Rp 1.122.262 — cth nota #5608, DP Rp 200.000 atas pcs Rp 435.000: nol
//     pcs tertutup, padahal keranjang menganggap DP-nya sudah terpakai.
//   • Bayar sebagian nota barang (bukan DP): 489 nota, 131 pelanggan,
//     Rp 58.268.957.
//
// Modul ini menghitung kedua sisa itu PER BARIS dengan cermin TS mesin
// alokasinya (`allocateInvoice`, src/lib/invoices.ts) — urutan kredit DP →
// diskon nota → uang FIFO yang sama, jadi tak ada aturan uang baru.
//
// Pcs lunas yang dipakai adalah yang TERCATAT (`invoice_lines.paid_qty`), bukan
// hasil hitung ulang: `paid_qty` monoton naik (turun hanya lewat refund/batal),
// dan keranjang memisahkan #Sudah Lunas dari angka tercatat itu. Sisa yang
// dihitung terhadap pcs hitung-ulang bisa menempel ke pcs yang di layar sudah
// "Lunas" — dua kali dipotong.
//
// Nilai pcs yang sudah lunas memakan KREDIT DP lebih dulu, baru uang — urutan
// yang sama dengan pass alokasinya. Jadi DP yang tersisa selalu bagian DP yang
// benar-benar belum menutup pcs, dan sisa uang tak pernah ikut terhitung DP.

/** Baris nota mentah (embed `invoice_lines`). */
export type ResidualLineLike = {
  id: string;
  kind: string;
  order_item_id?: string | null;
  order_id?: string | null;
  qty?: number | null;
  amount: number | string | null;
  paid_qty?: number | null;
  created_at: string;
  cancelled_at?: string | null;
};

/** Nota barang mentah beserta buku penerimaan & barisnya. */
export type ResidualInvoiceLike = {
  id: string;
  status: string;
  amount_paid?: number | string | null;
  balance_applied?: number | string | null;
  refunded_amount?: number | string | null;
  invoice_lines?: ReadonlyArray<ResidualLineLike> | null;
};

export type CartPaidResidual = {
  /**
   * Kredit DP lunas yang sudah dipasang di nota open/partial tapi belum jadi
   * pcs lunas, per pesanan — dikembalikan ke DP keranjang
   * (`cartDpCreditByOrder`), yang sebelumnya menganggapnya sudah terpakai.
   */
  dpByOrder: Map<string, number>;
  /**
   * Uang bayar nota barang (saldo + transfer − refund) yang menempel pcs
   * belum lunas item ini, belum genap satu pcs — per `order_items.id`.
   */
  moneyByItem: Map<string, number>;
};

/**
 * Status nota yang bisa menyimpan sisa: `open` (Kredit DP saja — nota tanpa
 * uang) & `partial`. Nota `paid` menutup penuh semua baris; `pending_acc`
 * belum dialokasikan, dan Kredit DP-nya memang belum dianggap terpakai
 * (`DP_CREDIT_REFLECTED_STATUSES`).
 */
export const PAID_RESIDUAL_STATUSES = ["open", "partial"] as const;

/** Di bawah satu rupiah = sisa pembulatan pro-rata diskon, bukan uang. */
const MIN_RESIDUAL = 1;

function num(v: unknown): number {
  const n = typeof v === "string" ? Number(v) : (v as number);
  return Number.isFinite(n) ? n : 0;
}

function add(map: Map<string, number>, key: string, value: number) {
  map.set(key, (map.get(key) ?? 0) + value);
}

export function cartPaidResidual(
  invoices: ReadonlyArray<ResidualInvoiceLike>
): CartPaidResidual {
  const statuses = new Set<string>(PAID_RESIDUAL_STATUSES);
  const dpByOrder = new Map<string, number>();
  const moneyByItem = new Map<string, number>();

  for (const inv of invoices ?? []) {
    if (!statuses.has(inv.status)) continue;
    const lines = (inv.invoice_lines ?? []).filter((l) => !l.cancelled_at);
    if (!lines.length) continue;
    const settled = Math.max(
      0,
      num(inv.balance_applied) + num(inv.amount_paid) - num(inv.refunded_amount)
    );
    const alloc = allocateInvoice(
      lines.map((l) => ({
        id: l.id,
        kind: l.kind as InvoiceLineKind,
        orderId: l.order_id ?? null,
        orderItemId: l.order_item_id ?? null,
        qty: l.qty ?? null,
        amount: num(l.amount),
        createdAt: l.created_at,
        cancelledAt: l.cancelled_at ?? null
      })),
      settled
    );

    for (const l of lines) {
      if (l.kind !== "product_item" || !l.order_item_id) continue;
      const qty = Math.floor(num(l.qty));
      const a = alloc.get(l.id);
      if (!a || qty <= 0) continue;
      // Harga baris SETELAH diskon nota — pembagi yang sama dengan mesinnya.
      const due = num(l.amount) - a.discountAbsorbed;
      if (due <= 0) continue;
      const paidPcs = Math.min(qty, Math.max(0, Math.floor(num(l.paid_qty))));
      const paidValue = (paidPcs * due) / qty;

      const dp = Math.max(0, a.creditAbsorbed - paidValue);
      const money = Math.max(0, a.moneyAbsorbed - Math.max(0, paidValue - a.creditAbsorbed));
      if (dp >= MIN_RESIDUAL && l.order_id) add(dpByOrder, l.order_id, dp);
      if (money >= MIN_RESIDUAL) add(moneyByItem, l.order_item_id, money);
    }
  }
  return { dpByOrder, moneyByItem };
}
